import React, { useState, useEffect } from 'react';
import Icon from '../../../../components/AppIcon';
import Input from '../../../../components/ui/Input';
import Button from '../../../../components/ui/Button';

const DateTimeControls = ({ onDateTimeChange, hrPermission = false }) => {
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [isEditing, setIsEditing] = useState(false);
  const [editedDate, setEditedDate] = useState('');
  const [editedTime, setEditedTime] = useState('');

  useEffect(() => {
    const timer = setInterval(() => {
      if (!isEditing) {
        setCurrentDateTime(new Date());
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isEditing]);

  useEffect(() => {
    onDateTimeChange(currentDateTime);
  }, [currentDateTime, onDateTimeChange]);

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const handleEdit = () => {
    const dateStr = currentDateTime?.toISOString()?.split('T')?.[0];
    const timeStr = currentDateTime?.toTimeString()?.split(' ')?.[0]?.substring(0, 5);
    setEditedDate(dateStr);
    setEditedTime(timeStr);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editedDate && editedTime) {
      const newDateTime = new Date(`${editedDate}T${editedTime}`);
      setCurrentDateTime(newDateTime);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedDate('');
    setEditedTime('');
  };

  if (isEditing) {
    return (
      <div className="w-full bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-elevation-2">
        <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
          <Icon name="Clock" size={20} className="md:w-6 md:h-6 lg:w-7 lg:h-7 text-primary" />
          <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground">Edit Date & Time</h3>
        </div>
        <div className="space-y-4 md:space-y-6 mb-4 md:mb-6">
          <Input
            type="date"
            label="Date"
            value={editedDate}
            onChange={(e) => setEditedDate(e?.target?.value)}
            required
          />

          <Input
            type="time"
            label="Time"
            value={editedTime}
            onChange={(e) => setEditedTime(e?.target?.value)}
            required
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
          <Button
            variant="default"
            onClick={handleSave}
            iconName="Check"
            iconPosition="left"
            fullWidth
          >
            Save Changes
          </Button>
          <Button
            variant="outline"
            onClick={handleCancel}
            fullWidth
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-elevation-2">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-2 md:gap-3">
          <Icon name="Clock" size={20} className="md:w-6 md:h-6 lg:w-7 lg:h-7 text-primary" />
          <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground">Date & Time</h3>
        </div>
        {hrPermission && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleEdit}
            iconName="Edit2"
            iconPosition="left"
          >
            Edit
          </Button>
        )}
      </div>

      <div className="space-y-3 md:space-y-4">
        <div className="flex items-center gap-3 md:gap-4 p-3 md:p-4 lg:p-5 bg-muted rounded-lg">
          <Icon name="Calendar" size={18} className="md:w-5 md:h-5 lg:w-6 lg:h-6 text-muted-foreground flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs md:text-sm text-muted-foreground mb-1">Date</p>
            <p className="text-sm md:text-base lg:text-lg font-semibold text-foreground">{formatDate(currentDateTime)}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 md:gap-4 p-3 md:p-4 lg:p-5 bg-muted rounded-lg">
          <Icon name="Clock" size={18} className="md:w-5 md:h-5 lg:w-6 lg:h-6 text-muted-foreground flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs md:text-sm text-muted-foreground mb-1">Time</p>
            <p className="text-sm md:text-base lg:text-lg font-semibold text-foreground">{formatTime(currentDateTime)}</p>
          </div>
        </div>
      </div>

      {!hrPermission && (
        <div className="mt-4 md:mt-6 p-3 md:p-4 bg-warning/10 rounded-lg flex items-start gap-2 md:gap-3">
          <Icon name="Info" size={16} className="md:w-5 md:h-5 text-warning flex-shrink-0 mt-0.5" />
          <p className="text-xs md:text-sm lg:text-base text-warning">Time editing requires HR admin permission</p>
        </div>
      )}
    </div>
  );
};

export default DateTimeControls;


